import { logger } from './utils';

/**
 * Central authority for authentication state
 * Manages auth state and notifies subscribers of changes
 */
class AuthStateManager {
  constructor() {
    this._state = {
      isAuthenticated: false,
      userId: null,
      profileId: null,
      // Additional auth properties
    };
    this._listeners = new Set();
    
    // Initialize from localStorage if available
    this._loadPersistedState();
  }

  // Update auth state and notify listeners
  updateState(newState) {
    this._state = { ...this._state, ...newState };
    this._persistState();
    this._notifyListeners();
    
    logger.debug('AuthState', 'State updated', { 
      isAuthenticated: this._state.isAuthenticated,
      userId: this._state.userId,
      profileId: this._state.profileId 
    });
    
    return this._state;
  }

  // Get current auth state (immutable copy)
  getState() {
    return { ...this._state };
  }

  // Subscribe to auth state changes
  subscribe(listener) {
    this._listeners.add(listener);
    
    // Immediately call the listener with current state
    listener(this.getState());
    
    // Return unsubscribe function
    return () => {
      this._listeners.delete(listener);
      logger.debug('AuthState', 'Listener unsubscribed');
    };
  }

  // Load state from localStorage
  _loadPersistedState() {
    try {
      const persisted = localStorage.getItem('auth_state');
      if (persisted) {
        const parsed = JSON.parse(persisted);
        this._state = { ...this._state, ...parsed };
        logger.debug('AuthState', 'Loaded persisted state', { 
          isAuthenticated: this._state.isAuthenticated,
          userId: this._state.userId,
          profileId: this._state.profileId 
        });
      }
    } catch (e) {
      logger.error('AuthState', 'Failed to load persisted auth state', e);
    }
  }

  // Persist certain auth fields to localStorage
  _persistState() {
    try {
      // Only persist non-sensitive fields
      const toPersist = {
        isAuthenticated: this._state.isAuthenticated,
        userId: this._state.userId,
        profileId: this._state.profileId
      };
      localStorage.setItem('auth_state', JSON.stringify(toPersist));
    } catch (e) {
      logger.error('AuthState', 'Failed to persist auth state', e);
    }
  }

  _notifyListeners() {
    const state = this.getState();
    this._listeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        logger.error('AuthState', 'Error in state listener', error);
      }
    });
  }
}

// Singleton instance
export const authState = new AuthStateManager();